import React, { FC } from "react"
import { graphql, Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import xss from "xss"

import { CardFragmentFragment, CardLpFragmentFragment, CardPostOrLpFragmentFragment } from "@graphqlTypes"

import { generateConditionalClassName, oneLineHelpScout } from "@utils"

import PlayButton from "@components/PlayButton/PlayButton"
import TextLink from "@components/TextLink/TextLink"

import Badge, { BADGE_COLORS } from "@components/Badge/Badge"
import { StringValueNode } from "graphql"
import ImageAboveFold from "@components/ImageAboveFold/ImageAboveFold"

import "./Card.scss"

export enum CARD_VARIANTS {
  DEFAULT = "DEFAULT",
  FEATURED = "FEATURED",
  FEATURED_HORIZONTAL = "FEATURED_HORIZONTAL",
  HORIZONTAL = "HORIZONTAL",
}

export type CardFragment = CardFragmentFragment | CardLpFragmentFragment | CardPostOrLpFragmentFragment | null

export type CardProps = {
  className?: string
  includeDescription?: boolean
  isAboveFold?: boolean
  item: CardFragment
  onClick?: (cardData: CardFragment) => void
  showSubcategory?: boolean
  variant?: CARD_VARIANTS
}

const Card: FC<CardProps> = ({
  className,
  includeDescription,
  onClick,
  isAboveFold,
  item,
  showSubcategory,
  variant = CARD_VARIANTS.DEFAULT,
}) => {
  const {
    category,
    description,
    image,
    imageBackgroundColor,
    permalink,
    seoDescription,
    subcategory,
    thumbnailImage,
    title,
    variant: postVariant,
    wistiaPreviewImage,
    wistiaVideoLength,
  } = {
    category: null,
    description: null,
    imageBackgroundColor: null,
    seoDescription: null,
    subcategory: null,
    variant: null,
    wistiaPreviewImage: null,
    wistiaVideoLength: null,
    ...item
  }

  const isFeatured = [CARD_VARIANTS.FEATURED, CARD_VARIANTS.FEATURED_HORIZONTAL].includes(variant)

  const imageToUse = thumbnailImage?.imgixImage?.gatsbyImageData ||
    wistiaPreviewImage?.imgixImage?.gatsbyImageData ||
    image?.imgixImage?.gatsbyImageData

  const overlineText = (showSubcategory && subcategory && subcategory[0]) ?
    subcategory[0].title :
    (category && category[0]) ?
      category[0].title :
      ""

  return !permalink ? null : (
    <Link
      className={generateConditionalClassName({
        Card: true,
        "no-description": !includeDescription,
        [`${className}`]: !!className,
        [`variant--${variant}`]: !!variant,
      })}
      onClick={onClick ? (() => onClick(item)) : undefined}
      to={permalink}
    >
      <figure className="Card--figure" style={{ background: imageBackgroundColor || "" }}>
        {postVariant === "WEBINAR" && (
          <>
            <span className="pill type--text-XS font-family--mono">
              {wistiaVideoLength || "Live Event"}
            </span>
            {wistiaVideoLength && <PlayButton />}
          </>
        )}
        {isAboveFold ? (
          <ImageAboveFold
            alt={title || ""}
            aspectRatio={16 / 9}
            imageData={imageToUse}
          />
        ) : (
          <GatsbyImage
            alt={title || ""}
            image={imageToUse}
            style={{ width: "100%", height: "100%" }}
          />
        )}
      </figure>
      <article>
        <header>
          {variant !== CARD_VARIANTS.HORIZONTAL && overlineText &&
            variant === CARD_VARIANTS.FEATURED_HORIZONTAL ? (
            <Badge color={BADGE_COLORS.CHARCOAL_TINT} useMonoFont>{overlineText}</Badge>
          ) : (
            <div className="type--mono-XS color--text-light">
              {overlineText}
            </div>
          )}
          {title && (
            isFeatured ?
              <h4 dangerouslySetInnerHTML={{ __html: xss(oneLineHelpScout(title)) }} /> :
              <h5 dangerouslySetInnerHTML={{ __html: xss(oneLineHelpScout(title)) }} />
          )}
        </header>
        {includeDescription && (description?.description || seoDescription) && (
          <p
            className={generateConditionalClassName({
              "Card--description": true,
              "color--text-default": true,
              "type--text-L": variant !== CARD_VARIANTS.HORIZONTAL,
              "type--text-S": variant === CARD_VARIANTS.HORIZONTAL,
            })}
            dangerouslySetInnerHTML={{
              __html: xss(description?.description || seoDescription || ""),
            }}
          />
        )}
        {variant === CARD_VARIANTS.FEATURED_HORIZONTAL && (
          <TextLink>Read more</TextLink>
        )}
      </article>
    </Link>
  )
}

export const CardFragment = graphql`
  fragment CardFragment on ContentfulPost {
    __typename
    contentful_id
    id
    date
    title
    permalink
    variant
    category {
      title
    }
    subcategory {
      title
    }
    description {
      description
    }
    imageBackgroundColor
    thumbnailImage {
      imgixImage {
        url
        gatsbyImageData(width: 814)
      }
    }
    image {
      imgixImage {
        url
        gatsbyImageData(width: 814)
      }
    }
    wistiaPreviewImage {
      imgixImage {
        url
        gatsbyImageData(width: 814)
      }
    }
    wistiaVideoLength
  }

  fragment CardLpFragment on ContentfulLandingPage {
    __typename
    contentful_id
    id
    title
    permalink
    category: postCategory {
      title
    }
    subcategory: postSubcategory {
      title
    }
    thumbnailImage: image {
      imgixImage {
        gatsbyImageData(width: 814)
      }
    }
    image: seoImage {
      imgixImage {
        gatsbyImageData(width: 814)
      }
    }
    seoDescription
  }

  fragment CardPostOrLpFragment on ContentfulPostOrLandingPage {
    __typename
    ... on ContentfulPost {
      ...CardFragment
    }
    ... on ContentfulLandingPage {
      ...CardLpFragment
    }
  }
`


export default Card
